import type { API, BlockAPI, ToolboxConfig } from '@editorjs/editorjs';
import type { SanitizerConfig } from '@editorjs/editorjs/types/configs';
import type {
  BlockTool,
  BlockToolConstructorOptions,
} from '@editorjs/editorjs/types/tools/block-tool';
import type { Root } from 'react-dom/client';
import type {
  FlotiqImageOpenModalFn,
  FlotiqImagePluginData,
  FlotiqImageSaveDataFn,
} from './components/FlotiqImagePluginBody';
import type {
  FlotiqImageSetting,
  FlotiqImageToggleTuneFn,
} from './components/FlotiqImagePluginSettings';

import { createRoot } from 'react-dom/client';

import FlotiqImagePluginBody from './components/FlotiqImagePluginBody';
import FlotiqImagePluginSettings from './components/FlotiqImagePluginSettings';

// Icons
import {
  AddBackgroundIcon,
  AddBorderIcon,
  ArrowLeftRightIcon,
} from '../../../../images/shapes';

import { mediaIcon } from '../../../RichText/icons';

interface FlotiqImageConfig {
  openModal?: FlotiqImageOpenModalFn;
}

interface FlotiqImageConstructorOptions extends BlockToolConstructorOptions<
  FlotiqImagePluginData,
  FlotiqImageConfig
> {
  block: BlockAPI;
}

export default class FlotiqImage implements BlockTool {
  private readonly readOnly: boolean;
  private config: FlotiqImageConfig;
  private readonly block: BlockAPI;
  private wrapper: HTMLDivElement | null;
  private root: Root | null = null;
  private data: FlotiqImagePluginData;
  private readonly api: API;
  private readonly settings: FlotiqImageSetting[];

  constructor({
    data,
    api,
    config,
    block,
    readOnly,
  }: FlotiqImageConstructorOptions) {
    this.readOnly = readOnly;

    this.config = config || {};
    this.block = block;
    this.wrapper = null;
    this.data = {
      url: 'url' in data ? data.url : '',
      extension: 'extension' in data ? data.extension : '',
      width: 'width' in data ? data.width : 0,
      height: 'height' in data ? data.height : 0,
      fileName: 'fileName' in data ? data.fileName : '',
      type: 'type' in data ? data.type : '',
      caption: 'caption' in data ? data.caption : '',
      withBorder: 'withBorder' in data ? data.withBorder : false,
      withBackground: 'withBackground' in data ? data.withBackground : false,
      stretched: 'stretched' in data ? data.stretched : false,
    };
    this.api = api;
    this.settings = [
      {
        name: 'withBorder',
        title: 'Add border',
        icon: <AddBorderIcon className="w-5!" />,
      },
      {
        name: 'stretched',
        title: 'Stretch image',
        icon: <ArrowLeftRightIcon className="w-5!" />,
      },
      {
        name: 'withBackground',
        title: 'Add background',
        icon: <AddBackgroundIcon className="w-5!" />,
      },
    ];
  }

  render(): HTMLDivElement {
    if (!this.wrapper) {
      this.wrapper = document.createElement('div');
    }
    if (!this.root) {
      this.root = createRoot(this.wrapper);
    }

    const saveData: FlotiqImageSaveDataFn = (data) => {
      this.data = data;
    };

    this.root.render(
      <FlotiqImagePluginBody
        openModal={this.config.openModal}
        saveSelected={saveData}
        data={this.data}
        block={this.block}
        api={this.api}
        disabled={this.readOnly}
      />,
    );
    return this.wrapper;
  }

  renderSettings(): HTMLDivElement {
    const wrapper = document.createElement('div');
    const root = createRoot(wrapper);

    const toggleTune: FlotiqImageToggleTuneFn = (tune) => {
      this.data[tune] = !this.data[tune];
      this._acceptTuneView();
      this.render();
    };

    root.render(
      <FlotiqImagePluginSettings
        settings={this.settings}
        api={this.api}
        toggleTune={toggleTune}
        block={this.block}
        tuneData={{
          withBorder: this.data.withBorder,
          withBackground: this.data.withBackground,
          stretched: this.data.stretched,
        }}
      />,
    );
    return wrapper;
  }

  static get toolbox(): ToolboxConfig {
    return {
      title: 'Media (images, videos, files)',
      icon: mediaIcon,
    };
  }

  save(blockContent: HTMLElement): FlotiqImagePluginData {
    const caption = blockContent.querySelector('[contenteditable]');
    this.data.caption = caption ? caption.innerHTML : '';
    return this.data;
  }

  validate(savedData: FlotiqImagePluginData): boolean {
    return savedData.url.trim().length > 0;
  }

  /**
   * Add specified class corresponds with activated tunes
   * @private
   */
  _acceptTuneView() {
    this.settings.forEach((tune) => {
      if (tune.name === 'stretched') {
        this.api.blocks.stretchBlock(
          this.api.blocks.getCurrentBlockIndex(),
          !!this.data.stretched,
        );
      }
    });
  }

  /**
   * Automatic sanitize config
   */
  static get sanitize(): SanitizerConfig {
    return {
      url: false, // disallow HTML
      caption: {}, // only tags from Inline Toolbar
    };
  }

  static get isReadOnlySupported(): boolean {
    return true;
  }
}
